/**
 * Content sections to add value for AdSense compliance
 */

export const HowItWorks = () => {
  const steps = [
    {
      number: '1',
      title: 'Upload Your SVG',
      description: 'Drag and drop your SVG file into the upload area, or click to browse your files. You can also paste SVG code directly from your clipboard.',
    },
    {
      number: '2',
      title: 'Preview & Verify',
      description: 'See a live preview of your SVG to ensure it displays correctly. The tool validates your SVG structure automatically.',
    },
    {
      number: '3',
      title: 'Get Clean Code',
      description: 'Your SVG is converted to clean, optimized code with syntax highlighting. Copy it with one click or download as a file.',
    },
  ];

  return (
    <section className="py-12 bg-slate-800/50" aria-labelledby="how-it-works">
      <div className="max-w-7xl mx-auto px-4">
        <h2 id="how-it-works" className="text-2xl font-bold text-white text-center mb-8">
          How It Works
        </h2>
        <div className="grid md:grid-cols-3 gap-8">
          {steps.map((step) => (
            <div key={step.number} className="text-center">
              <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center text-white text-xl font-bold mx-auto mb-4">
                {step.number}
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{step.title}</h3>
              <p className="text-slate-400">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const Features = () => {
  const features = [
    {
      icon: '⚡',
      title: 'Instant Conversion',
      description: 'Convert SVG files to code in milliseconds. No server processing required - everything happens in your browser.',
    },
    {
      icon: '🔒',
      title: 'Privacy First',
      description: 'Your files never leave your device. All processing is done locally in your browser for maximum security.',
    },
    {
      icon: '📋',
      title: 'One-Click Copy',
      description: 'Copy your optimized SVG code to clipboard instantly. Ready to paste into your HTML, CSS, or JavaScript.',
    },
    {
      icon: '🎨',
      title: 'Syntax Highlighting',
      description: 'View your SVG code with beautiful syntax highlighting that makes it easy to read and understand.',
    },
    {
      icon: '📱',
      title: 'Fully Responsive',
      description: 'Works perfectly on desktop, tablet, and mobile devices. Convert SVGs anywhere, anytime.',
    },
    {
      icon: '🆓',
      title: '100% Free',
      description: 'No sign-up required, no limits, no watermarks. Use ImageLab as much as you need.',
    },
  ];

  return (
    <section className="py-12" aria-labelledby="features">
      <div className="max-w-7xl mx-auto px-4">
        <h2 id="features" className="text-2xl font-bold text-white text-center mb-2">
          Why Choose ImageLab?
        </h2>
        <p className="text-slate-400 text-center mb-8 max-w-2xl mx-auto">
          The fastest, most secure way to convert SVG files to clean, optimized code for your web projects.
        </p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature) => (
            <div key={feature.title} className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
              <div className="text-3xl mb-3">{feature.icon}</div>
              <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
              <p className="text-slate-400 text-sm">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const ComingSoon = () => {
  const upcomingFeatures = [
    {
      icon: '🎯',
      title: 'SVG Optimizer',
      description: 'Optimize and minify your SVG files to reduce file size while maintaining quality.',
    },
    {
      icon: '✏️',
      title: 'SVG Editor',
      description: 'Edit SVG properties like colors, dimensions, and paths directly in the browser.',
    },
    {
      icon: '🔄',
      title: 'Batch Conversion',
      description: 'Convert multiple images at once and download them all as a ZIP file.',
    },
  ];

  return (
    <section className="py-12 bg-gradient-to-b from-slate-900 to-slate-800" aria-labelledby="coming-soon">
      <div className="max-w-7xl mx-auto px-4">
        <h2 id="coming-soon" className="text-2xl font-bold text-white text-center mb-2">
          Coming Soon
        </h2>
        <p className="text-slate-400 text-center mb-8 max-w-2xl mx-auto">
          We're working on exciting new features to make ImageLab even more powerful.
        </p>
        <div className="grid md:grid-cols-3 gap-6">
          {upcomingFeatures.map((feature) => (
            <div key={feature.title} className="bg-slate-800/50 rounded-xl p-6 border border-indigo-500/30 relative overflow-hidden">
              <div className="absolute top-3 right-3">
                <span className="bg-indigo-600 text-white text-xs px-2 py-1 rounded-full font-medium">
                  Soon
                </span>
              </div>
              <div className="text-3xl mb-3">{feature.icon}</div>
              <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
              <p className="text-slate-400 text-sm">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const FAQ = () => {
  const faqs = [
    {
      question: 'What is an SVG file?',
      answer: 'SVG (Scalable Vector Graphics) is an XML-based image format for two-dimensional graphics. Unlike raster images (PNG, JPG), SVGs can scale to any size without losing quality, making them perfect for logos, icons, and illustrations on the web.',
    },
    {
      question: 'Why should I convert SVG to code?',
      answer: 'Embedding SVG code directly in your HTML allows for better performance (no additional HTTP requests), easier styling with CSS, JavaScript manipulation, and smaller file sizes. It also enables features like animations and interactivity.',
    },
    {
      question: 'Is my data secure?',
      answer: 'Absolutely! ImageLab processes all files locally in your browser. Your SVG files are never uploaded to any server, ensuring complete privacy and security for your designs.',
    },
    {
      question: 'What file formats are supported?',
      answer: 'Currently, ImageLab supports SVG files (.svg). We process standard SVG 1.1 and SVG 2.0 formats, including complex paths, gradients, filters, and embedded fonts.',
    },
    {
      question: 'Can I use this tool for commercial projects?',
      answer: 'Yes! ImageLab is completely free to use for both personal and commercial projects. There are no usage limits or restrictions.',
    },
    {
      question: 'How do I use the converted SVG code?',
      answer: 'Simply copy the generated code and paste it directly into your HTML file where you want the SVG to appear. You can also save it as a separate file and reference it using an <img> tag or CSS background.',
    },
  ];

  return (
    <section className="py-12 bg-slate-800/50" aria-labelledby="faq">
      <div className="max-w-4xl mx-auto px-4">
        <h2 id="faq" className="text-2xl font-bold text-white text-center mb-8">
          Frequently Asked Questions
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <details key={index} className="bg-slate-900/50 rounded-lg border border-slate-700 group">
              <summary className="px-6 py-4 cursor-pointer text-white font-medium list-none flex justify-between items-center">
                {faq.question}
                <span className="text-indigo-400 group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <div className="px-6 pb-4 text-slate-400">
                {faq.answer}
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
};

export const About = () => {
  return (
    <section className="py-12" aria-labelledby="about">
      <div className="max-w-4xl mx-auto px-4">
        <h2 id="about" className="text-2xl font-bold text-white text-center mb-6">
          About ImageLab
        </h2>
        <div className="prose prose-invert max-w-none text-slate-300 space-y-4">
          <p>
            ImageLab is a free online tool designed for web developers, designers, and anyone who works with vector graphics. Our mission is to simplify the process of converting SVG files into clean, optimized code that you can use directly in your web projects.
          </p>
          <p>
            Whether you're building a website, creating a web application, or working on a design system, ImageLab helps you work more efficiently. Instead of manually copying SVG code from design tools or dealing with messy exports, simply drag and drop your files and get perfectly formatted code in seconds.
          </p>
          <p>
            Built with modern web technologies including React and TypeScript, ImageLab runs entirely in your browser. This means your files are never uploaded to a server – everything stays on your device, ensuring your designs remain private and secure.
          </p>
          <h3 className="text-xl font-semibold text-white mt-8 mb-4">Perfect For:</h3>
          <ul className="list-disc list-inside space-y-2 text-slate-400">
            <li>Frontend developers integrating icons and illustrations</li>
            <li>UI/UX designers exporting assets for development</li>
            <li>Graphic designers preparing web-ready graphics</li>
            <li>Marketing teams creating email templates with SVG graphics</li>
            <li>Anyone who needs to convert SVG files to inline code</li>
          </ul>
        </div>
      </div>
    </section>
  );
};
